import { PasswordInput } from "@/components/password-input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useAuth } from "@/modules/authentication/context/AuthContext";
import { AlertCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import GoogleLoginButton from "./GoogleLoginButton";
import ZaloLoginButton from "./ZaloLoginButton";

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"form">) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isZaloLoading, setIsZaloLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const result = await login(username, password, rememberMe);

      if (result.success) {
        // Luôn chuyển tới danh sách thiết bị sau khi đăng nhập thành công.
        navigate("/monitoring", { replace: true });
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.log(err);
      setError("Đã xảy ra lỗi, vui lòng thử lại!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      className={cn(
        "flex flex-col gap-6 p-6 bg-white rounded-lg border border-gray-200",
        className
      )}
      onSubmit={handleSubmit}
      {...props}
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">Đăng nhập hệ thống</h1>
        <p className="text-muted-foreground text-sm text-balance">
          Nhập thông tin đăng nhập để truy cập hệ thống
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      <div className="grid gap-6">
        <div className="grid gap-3">
          <Label htmlFor="username">Tên đăng nhập</Label>
          <Input
            id="username"
            type="text"
            name="username"
            autoComplete="username"
            placeholder="Nhập tên đăng nhập"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>
        <div className="grid gap-3">
          <div className="flex items-center">
            <Label htmlFor="password">Mật khẩu</Label>
          </div>
          <PasswordInput
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="rememberMe"
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(!!checked)}
            />
            <label
              htmlFor="rememberMe"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Ghi nhớ tài khoản
            </label>
          </div>
          <Link
            to="/forgot-password"
            className="text-sm text-primary underline"
          >
            Quên mật khẩu
          </Link>
        </div>
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Đang đăng nhập...
            </>
          ) : (
            "Đăng nhập"
          )}
        </Button>
        <div className="flex items-center justify-center gap-2">
          <span className="text-muted-foreground">Đăng nhập bằng</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <ZaloLoginButton
            rememberMe={rememberMe}
            isZaloLoading={isZaloLoading}
            setIsZaloLoading={setIsZaloLoading}
            setError={setError}
            disabled={isLoading || isGoogleLoading || isZaloLoading}
          />
          <GoogleLoginButton
            rememberMe={rememberMe}
            setIsGoogleLoading={setIsGoogleLoading}
            setError={setError}
            disabled={isLoading || isGoogleLoading || isZaloLoading}
          />
        </div>
        <div className=" text-sm">
          <span className="text-muted-foreground">
            Nếu bạn chưa có tài khoản hãy đăng ký{" "}
          </span>
          <Link to="/register" className="font-medium  underline text-primary">
            tại đây
          </Link>
        </div>
      </div>
    </form>
  );
}
